import type { ExecutionTranscriptView } from '@/lib/api/generated/model';

/**
 * One thing Claude did during a run, taken directly from the generated
 * contract rather than re-declared here — `orval` inlines the backend's
 * discriminated union onto the transcript field rather than naming it, so
 * this alias is what every consumer in this feature imports instead of
 * repeating the inline union type.
 *
 * Derived from `ExecutionTranscriptView` since spec 081: transcripts left
 * `TaskExecutionSummary`, because the workspace aggregate that carries it is
 * polled every two seconds by every open board and no board surface renders
 * one. The entry shape is unchanged — only where it is read from.
 */
export type TranscriptEntry = NonNullable<
  ExecutionTranscriptView['transcript']
>[number];

export interface SplitTranscript {
  main: TranscriptEntry[];
  transitionSelection: TranscriptEntry[] | null;
}

/**
 * Separates one execution's transcript into the main run and, where present,
 * the distinct transition-selection sub-session (history.txt decision 3).
 *
 * `phase` is required on every entry the backend emits, so this is a plain
 * partition, not a fallback for missing data.
 */
export function splitTranscriptPhases(
  entries: TranscriptEntry[],
): SplitTranscript {
  const main: TranscriptEntry[] = [];
  const transitionSelection: TranscriptEntry[] = [];

  for (const entry of entries) {
    if (entry.phase === 'transition_selection') {
      transitionSelection.push(entry);
    } else {
      main.push(entry);
    }
  }

  return {
    main,
    transitionSelection:
      transitionSelection.length > 0 ? transitionSelection : null,
  };
}

/** True only for a `tool_result` entry recorded as an error. */
export function isErrorResult(entry: TranscriptEntry): boolean {
  return entry.type === 'tool_result' && entry.is_error;
}
